import React from 'react'
import { Formik, FormikProps } from 'formik'
import { useMutation } from 'react-query'
import toast from 'react-hot-toast'
import * as Yup from 'yup'

import CustomerForm from '../Customers/CustomerForm'
import { NewCustomerTDO } from '../../types/customer'
import customerService from '../../services/customerService'
import { queryClient } from '../../utils/react-query'
import { FILE_SIZE, SUPPORTED_FORMATS } from '../../utils/config'
import { useUser } from '../../hooks/useUser'
import { useCustomer } from '../../hooks/useCustomer'
import { BuzzlUsersError } from '../../types/buzzl-user'

const schema = Yup.object().shape({
  name: Yup.string().required(),
  adminEmail: Yup.string().email().required(),
  fallbackDescription: Yup.string().required(),
  fallbackActionText: Yup.string().required(),
  fallbackActionUrl: Yup.string().url().required(),
  fallbackImage: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
  avatar: Yup.mixed()
    .test('fileSize', 'File too large', (value) => !value || (value && value.size <= FILE_SIZE))
    .test('fileFormat', 'Unsupported Format', (value) => !value || (value && SUPPORTED_FORMATS.includes(value.type))),
})

const BrandDetails: React.FC = () => {
  const user = useUser()
  const customer = useCustomer(user.data?.customerUser?.customer?.id)
  const updateCustomer = useMutation(customerService.updateCustomer, {
    onSuccess: async () => {
      queryClient.refetchQueries('customers')
      queryClient.refetchQueries('customer')
      toast.success('Customer Updated.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  if (user.isLoading || customer.isLoading) {
    return <p>Loading...</p>
  }

  const initialValues: Partial<NewCustomerTDO> = {
    name: customer.data?.name || '',
    adminEmail: customer.data?.adminEmail || '',
    fallbackDescription: customer.data?.fallbackDescription || '',
    fallbackActionText: customer.data?.fallbackActionText || '',
    fallbackActionUrl: customer.data?.fallbackActionUrl || '',
    fallbackImage: undefined,
    avatar: undefined,
  }

  const handleOnSubmit = async ({ avatar, fallbackImage, places, ...rest }: Partial<NewCustomerTDO>) => {
    const obj: any = {
      ...rest,
    }

    if (avatar) {
      obj.avatar = await customerService.uploadImage({
        file: avatar,
      })
    }

    if (fallbackImage) {
      obj.fallbackImage = await customerService.uploadImage({
        file: fallbackImage,
      })
    }

    await updateCustomer.mutate({ ...obj, id: user.data?.customerUser?.customer?.id })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ setFieldValue, isSubmitting, values, dirty }: FormikProps<Partial<NewCustomerTDO>>) => {
        return (
          <CustomerForm
            customerId={user.data?.customerUser?.customer?.id}
            isSubmitting={isSubmitting}
            setFieldValue={setFieldValue}
            values={values}
            currentAvatar={customer.data?.avatar?.url}
            currentFallbackImage={customer.data?.fallbackImage?.url}
            isDisabled={isSubmitting || !dirty}
          />
        )
      }}
    </Formik>
  )
}

export default BrandDetails
